import React, { useState, useEffect, useCallback, useRef } from "react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

/**
 * Inline renderer for a message's MMS attachment. Twilio-hosted inbound
 * media URLs are directly fetchable, so images and video render in the
 * bubble; anything else (vCard, audio, PDF) falls back to a link.
 */
function MediaAttachment({ url, type }) {
  if (!url) return null;
  const kind = (type || "").split("/")[0];
  if (kind === "image") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-2">
        <img
          src={url}
          alt="MMS attachment"
          className="max-w-full max-h-64 rounded-lg border border-gray-200"
          loading="lazy"
        />
      </a>
    );
  }
  if (kind === "video") {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        className="block mt-2 max-w-full max-h-64 rounded-lg border border-gray-200"
      />
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="block mt-2 text-xs underline opacity-80"
    >
      📎 attachment{type ? ` (${type})` : ""}
    </a>
  );
}

export default function ReplyInbox() {
  const { user } = useAuth();

  // Data state
  const [replies, setReplies] = useState([]);
  const [selectedReply, setSelectedReply] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [tags, setTags] = useState([]);

  // Filter state
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [tagFilter, setTagFilter] = useState("all"); // all, tagged, untagged
  const [selectedTagFilter, setSelectedTagFilter] = useState(""); // specific tag id

  // Reply compose
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [tagNote, setTagNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(null); // tag id

  // UI state
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const replyInputRef = useRef(null);
  const listRef = useRef(null);

  // Load campaigns on mount
  useEffect(() => {
    api.get("/campaigns").then((res) => setCampaigns(res.data.campaigns || [])).catch(() => {});
  }, []);

  // Load replies
  const loadReplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("per_page", "30");
      if (selectedCampaign) params.append("campaign_id", selectedCampaign);
      if (tagFilter !== "all") params.append("tag_status", tagFilter);
      if (selectedTagFilter) params.append("tag_id", selectedTagFilter);

      const res = await api.get(`/replies?${params.toString()}`);
      setReplies(res.data.replies || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to load replies:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedCampaign, tagFilter, selectedTagFilter, page]);

  useEffect(() => {
    loadReplies();
  }, [loadReplies]);

  // Load tags when campaign changes
  useEffect(() => {
    if (!selectedCampaign) {
      setTags([]);
      return;
    }
    api.get(`/survey-tags/campaigns/${selectedCampaign}/tags`)
      .then((res) => setTags(res.data.tags || []))
      .catch(() => setTags([]));
  }, [selectedCampaign]);

  // Also load tags from the selected reply's campaign
  useEffect(() => {
    if (selectedReply?.campaign_id && !selectedCampaign) {
      api.get(`/survey-tags/campaigns/${selectedReply.campaign_id}/tags`)
        .then((res) => setTags(res.data.tags || []))
        .catch(() => {});
    }
  }, [selectedReply, selectedCampaign]);

  // Send reply
  const handleSendReply = async () => {
    if (!selectedReply || !replyText.trim() || sending) return;

    setSending(true);
    try {
      await api.post("/messages/send", {
        campaign_id: selectedReply.campaign_id,
        contact_id: selectedReply.contact_id,
        body: replyText.trim(),
        hold_duration_ms: 200,
        input_method: "keyboard",
      });
      setReplyText("");
      loadReplies();
    } catch (err) {
      alert(errorToUserMessage(err, "Reply failed"));
    } finally {
      setSending(false);
    }
  };

  // Apply tag
  const handleApplyTag = async (tagId) => {
    if (!selectedReply?.campaign_contact_id) return;

    // Check if already applied
    const alreadyApplied = selectedReply.applied_tags?.some((t) => t.id === tagId);

    if (alreadyApplied) {
      // Remove tag
      try {
        await api.post("/replies/untag", {
          campaign_contact_id: selectedReply.campaign_contact_id,
          survey_tag_id: tagId,
        });
        // Update local state
        setSelectedReply((prev) => ({
          ...prev,
          applied_tags: prev.applied_tags.filter((t) => t.id !== tagId),
        }));
        setReplies((prev) =>
          prev.map((r) =>
            r.id === selectedReply.id
              ? { ...r, applied_tags: r.applied_tags.filter((t) => t.id !== tagId) }
              : r
          )
        );
      } catch (err) {
        console.error("Untag failed:", err);
      }
    } else {
      // Apply tag
      try {
        const res = await api.post("/replies/tag", {
          campaign_contact_id: selectedReply.campaign_contact_id,
          survey_tag_id: tagId,
          notes: showNoteInput === tagId ? tagNote : undefined,
        });
        const tag = tags.find((t) => t.id === tagId);
        const newTag = {
          id: tagId,
          name: tag?.name || "Tag",
          color: tag?.color || "#3B82F6",
          response_id: res.data.response?.id,
          tagged_at: new Date().toISOString(),
          notes: showNoteInput === tagId ? tagNote : null,
        };
        setSelectedReply((prev) => ({
          ...prev,
          applied_tags: [...(prev.applied_tags || []), newTag],
        }));
        setReplies((prev) =>
          prev.map((r) =>
            r.id === selectedReply.id
              ? { ...r, applied_tags: [...(r.applied_tags || []), newTag] }
              : r
          )
        );
        setTagNote("");
        setShowNoteInput(null);
      } catch (err) {
        console.error("Tag failed:", err);
      }
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (document.activeElement === replyInputRef.current) return;

      // Number keys 1-9 for tagging
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9 && num <= tags.length && selectedReply) {
        e.preventDefault();
        handleApplyTag(tags[num - 1].id);
        return;
      }

      // J/K for navigation
      if (e.key === "j" || e.key === "k") {
        e.preventDefault();
        const currentIdx = replies.findIndex((r) => r.id === selectedReply?.id);
        const nextIdx = e.key === "j" ? currentIdx + 1 : currentIdx - 1;
        if (nextIdx >= 0 && nextIdx < replies.length) {
          setSelectedReply(replies[nextIdx]);
        }
        return;
      }

      // R to focus reply input
      if (e.key === "r" && selectedReply) {
        e.preventDefault();
        replyInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [tags, selectedReply, replies]); // eslint-disable-line

  if (!user) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reply Inbox</h1>
        <div className="flex items-center space-x-2">
          <select
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={selectedCampaign}
            onChange={(e) => { setSelectedCampaign(e.target.value); setPage(1); }}
          >
            <option value="">All Campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={tagFilter}
            onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
          >
            <option value="all">All Replies</option>
            <option value="tagged">Tagged</option>
            <option value="untagged">Untagged</option>
          </select>
          {tags.length > 0 && (
            <select
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              value={selectedTagFilter}
              onChange={(e) => { setSelectedTagFilter(e.target.value); setPage(1); }}
            >
              <option value="">Any Tag</option>
              {tags.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={loadReplies}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: "600px" }}>

        {/* Reply list (4 cols) */}
        <div className="lg:col-span-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">
              {loading ? "Loading..." : `${total} Replies`}
            </h3>
            <span className="text-xs text-gray-400">
              J/K to navigate
            </span>
          </div>
          <div ref={listRef} className="divide-y divide-gray-100 flex-1 overflow-y-auto">
            {replies.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-400 text-sm">
                  {loading ? "Loading replies..." : "No replies match your filters"}
                </p>
              </div>
            ) : (
              replies.map((reply) => (
                <button
                  key={reply.id}
                  onClick={() => setSelectedReply(reply)}
                  className={`w-full text-left p-3 hover:bg-blue-50 transition-colors ${
                    selectedReply?.id === reply.id ? "bg-blue-50 border-l-4 border-blue-500" : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">
                        {reply.contact_name || reply.phone_number}
                      </span>
                      {reply.is_opted_out && (
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                          OPT-OUT
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                      {new Date(reply.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate mt-1">
                    {reply.media_url && <span title="Has attachment">📎 </span>}
                    {reply.body || (reply.media_url ? "(media only)" : "")}
                  </p>
                  <div className="flex items-center space-x-1 mt-1.5 flex-wrap gap-1">
                    {reply.campaign_name && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {reply.campaign_name}
                      </span>
                    )}
                    {reply.applied_tags?.map((tag) => (
                      <span
                        key={tag.id}
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: tag.color + "20", color: tag.color }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </button>
              ))
            )}
          </div>
          {/* Pagination */}
          {total > 30 && (
            <div className="p-2 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="text-xs text-blue-600 font-medium disabled:text-gray-300"
              >
                Previous
              </button>
              <span className="text-xs text-gray-400">Page {page}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={replies.length < 30}
                className="text-xs text-blue-600 font-medium disabled:text-gray-300"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Conversation + tagging (8 cols) */}
        <div className="lg:col-span-8 flex flex-col space-y-4">
          {selectedReply ? (
            <>
              {/* Contact header */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {selectedReply.contact_name || "Unknown"}
                      </h3>
                      {selectedReply.is_opted_out && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
                          OPTED OUT
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 font-mono">{selectedReply.phone_number}</p>
                    {selectedReply.campaign_name && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Campaign: {selectedReply.campaign_name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Applied tags display */}
                {selectedReply.applied_tags?.length > 0 && (
                  <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Tags:</span>
                    {selectedReply.applied_tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center text-xs px-2 py-1 rounded-full font-medium"
                        style={{ backgroundColor: tag.color + "20", color: tag.color }}
                      >
                        {tag.name}
                        {tag.notes && (
                          <span className="ml-1 opacity-70" title={tag.notes}>*</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Tag buttons */}
              {tags.length > 0 && selectedReply.campaign_contact_id && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                      Survey Tags
                    </h4>
                    <span className="text-xs text-gray-400">Press 1-9 to quick-tag</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, idx) => {
                      const isApplied = selectedReply.applied_tags?.some((t) => t.id === tag.id);
                      return (
                        <div key={tag.id} className="flex items-center">
                          <button
                            onClick={() => handleApplyTag(tag.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                              isApplied
                                ? "shadow-sm"
                                : "border-transparent opacity-70 hover:opacity-100"
                            }`}
                            style={{
                              backgroundColor: isApplied ? tag.color + "20" : "transparent",
                              borderColor: isApplied ? tag.color : "transparent",
                              color: tag.color,
                            }}
                            title={`Press ${idx + 1} to ${isApplied ? "remove" : "apply"}`}
                          >
                            <span className="text-xs opacity-50 mr-1">{idx + 1}</span>
                            {tag.name}
                            {isApplied && " \u2713"}
                          </button>
                          {!isApplied && (
                            <button
                              onClick={() => setShowNoteInput(showNoteInput === tag.id ? null : tag.id)}
                              className="text-xs text-gray-400 hover:text-gray-600 ml-1"
                              title="Add note"
                            >
                              +note
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {showNoteInput && (
                    <div className="mt-2 flex items-center space-x-2">
                      <input
                        type="text"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                        placeholder="Tag note (optional)..."
                        value={tagNote}
                        onChange={(e) => setTagNote(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleApplyTag(showNoteInput);
                          }
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => handleApplyTag(showNoteInput)}
                        className="text-sm text-blue-600 font-medium hover:text-blue-800"
                      >
                        Apply with note
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Conversation */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                  {selectedReply.conversation?.length > 0 ? (
                    selectedReply.conversation.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-md px-4 py-2.5 rounded-2xl text-sm ${
                            msg.direction === "outbound"
                              ? "bg-blue-600 text-white rounded-br-md"
                              : "bg-gray-100 text-gray-900 rounded-bl-md"
                          }`}
                        >
                          {msg.body && <p>{msg.body}</p>}
                          <MediaAttachment url={msg.media_url} type={msg.media_type} />
                          <div className={`flex items-center space-x-2 text-xs mt-1 ${
                            msg.direction === "outbound" ? "text-blue-200" : "text-gray-400"
                          }`}>
                            <span>{new Date(msg.created_at).toLocaleString()}</span>
                            {msg.status && msg.direction === "outbound" && (
                              <span className={
                                msg.status === "delivered" ? "text-green-300" :
                                msg.status === "failed" ? "text-red-300" :
                                ""
                              }>
                                {msg.status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-start">
                      <div className="max-w-md px-4 py-2.5 rounded-2xl rounded-bl-md bg-gray-100 text-gray-900 text-sm">
                        {selectedReply.body && <p>{selectedReply.body}</p>}
                        <MediaAttachment
                          url={selectedReply.media_url}
                          type={selectedReply.media_type}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Reply input */}
                {!selectedReply.is_opted_out ? (
                  <div className="p-4 border-t border-gray-200">
                    <div className="flex space-x-2">
                      <input
                        ref={replyInputRef}
                        type="text"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Type a reply... (R to focus)"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSendReply()}
                      />
                      <button
                        onClick={handleSendReply}
                        disabled={!replyText.trim() || sending}
                        className="bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
                      >
                        {sending ? "Sending..." : "Send"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-t border-gray-200 bg-red-50">
                    <p className="text-sm text-red-600 font-medium text-center">
                      This contact has opted out. Replies are disabled.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-400 text-sm">Select a reply to view the conversation</p>
                <p className="text-gray-300 text-xs mt-1">Use J/K keys to navigate</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Keyboard shortcut help */}
      <div className="text-xs text-gray-400 flex items-center space-x-4">
        <span><strong>J/K</strong> navigate replies</span>
        <span><strong>R</strong> focus reply input</span>
        <span><strong>1-9</strong> apply/remove tags</span>
        <span><strong>Enter</strong> send reply</span>
      </div>
    </div>
  );
}
